import { Flame, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import QuickIpCheck from "@/components/RefClick/QuickIpCheck";

/**
 * Hero summary strip at the top of the panel.
 *
 * Marker ``SummaryBar_30_4`` (near line 30) is where the owner asked for
 * a quick IP fraud + duplicate check control. It lives on the right side
 * of row 1 next to the streak badge — compact (one icon that expands
 * inline on desktop, direct-opens modal on mobile).
 */
export default function SummaryBar({ summary, error, loading, onOpenIpCheck }) {
  const total = summary?.today_total ?? 0;
  const target = summary?.today_target_total ?? 0;
  const pct = target > 0 ? Math.min(100, Math.round((total / target) * 100)) : 0;
  const streak = summary?.streak ?? 0;
  const allMet = !!summary?.all_targets_met && target > 0;
  const remaining = Math.max(0, target - total);

  return (
    <section
      className="border-b border-white/5 bg-white/[0.02] px-3 py-3 sm:px-5 sm:py-4"
      data-testid="summary-bar"
    >
      {/* Row 1 — label + big counter + % pill + streak */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-baseline gap-3 sm:gap-4">
          <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-text-muted sm:text-xs">
            Today
          </span>
          <div className="flex items-baseline gap-1">
            {/* The count itself is keyed → spring-pops on every server refetch */}
            <motion.span
              key={total}
              initial={{ scale: 1.35, y: -2 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 16 }}
              className={
                "font-mono-num text-3xl leading-none sm:text-4xl " +
                (allMet ? "text-accent" : "text-text-main")
              }
              data-testid="summary-today-total"
              style={
                allMet
                  ? { textShadow: "0 0 20px rgb(var(--accent) / 0.55)" }
                  : undefined
              }
            >
              {total}
            </motion.span>
            <span className="font-mono-num text-2xl leading-none text-text-muted sm:text-3xl">
              /{target}
            </span>
          </div>
          {target > 0 && (
            <span
              className="hidden font-mono-num text-xs text-text-muted sm:inline"
              data-testid="summary-percent"
            >
              {pct}%
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <AnimatePresence>
            {streak > 0 && (
              <motion.div
                key="streak"
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.7 }}
                transition={{ type: "spring", stiffness: 380, damping: 20 }}
                data-testid="streak-badge"
                className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-2.5 py-1 font-mono-num text-xs text-accent"
                style={{
                  boxShadow: "0 0 14px rgb(var(--accent) / 0.28)",
                }}
              >
                <Flame className="h-3.5 w-3.5" />
                Day {streak}
              </motion.div>
            )}
          </AnimatePresence>
          {/* Marker SummaryBar_30_4: quick IP fraud + duplicate check. */}
          <QuickIpCheck onOpenModal={onOpenIpCheck} />
        </div>
      </div>

      {/* Row 2 — thick progress bar with animated fill + shimmer */}
      <div className="mt-4 sm:mt-5">
        <div className="relative h-3 w-full overflow-hidden rounded-full bg-white/[0.06] ring-1 ring-white/5">
          <motion.div
            className={
              "progress-shimmer h-full rounded-full " +
              (allMet ? "" : "bg-accent")
            }
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ type: "spring", stiffness: 110, damping: 22, mass: 0.6 }}
            style={{
              boxShadow: allMet
                ? "0 0 24px rgb(var(--success) / 0.55), 0 0 12px rgb(var(--accent) / 0.35)"
                : "0 0 14px rgb(var(--accent) / 0.55)",
              background: allMet
                ? "linear-gradient(90deg, rgb(var(--accent)) 0%, rgb(var(--success)) 100%)"
                : undefined,
            }}
          />
          {/* One-shot bloom pulse the moment we hit 100 %. */}
          <AnimatePresence>
            {allMet && (
              <motion.div
                key="bloom"
                initial={{ opacity: 0.7, scaleX: 1, scaleY: 1 }}
                animate={{ opacity: 0, scaleX: 1.05, scaleY: 1.8 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="pointer-events-none absolute inset-0 rounded-full"
                style={{
                  background:
                    "linear-gradient(90deg, rgb(var(--accent)) 0%, rgb(var(--success)) 100%)",
                  filter: "blur(6px)",
                  transformOrigin: "center",
                }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Row 3 — hint text / error / loading */}
      <div className="mt-3 flex items-center justify-between gap-3">
        <p
          className="min-w-0 truncate text-xs text-text-muted sm:text-sm"
          data-testid="summary-hint"
        >
          {loading && !summary ? (
            <span>Loading today&apos;s totals…</span>
          ) : target === 0 ? (
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              Add a link to start tracking
            </span>
          ) : allMet ? (
            <span
              className="inline-flex items-center gap-1.5 font-mono-num text-accent"
              data-testid="summary-done"
              style={{ textShadow: "0 0 10px rgb(var(--accent) / 0.5)" }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              All done for today ✓
            </span>
          ) : total === 0 ? (
            <span>
              <span className="font-mono-num text-text-main">{target}</span>{" "}
              clicks to go — first one&apos;s the hardest
            </span>
          ) : (
            <span>
              <span className="font-mono-num text-text-main">{remaining}</span>{" "}
              click{remaining === 1 ? "" : "s"} left today
            </span>
          )}
        </p>
        {error && (
          <span
            className="font-mono-safe shrink-0 text-[11px] text-red-400/80"
            data-testid="summary-error"
            title={error}
          >
            {error}
          </span>
        )}
      </div>
    </section>
  );
}
